// src/pages/ProviderApplication.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { submitProviderApplication, getApplicationStatus, APPLICATION_STATUS } from '../services/UserRolesService';
import { geocodeAddress } from '../services/GeocodingService';
import { uploadMultipleMedia } from '../services/StorageService';
import MultiMediaUpload from '../components/MultiMediaUpload';
import LocationAutocomplete from '../components/LocationAutocomplete';
import './ProviderApplication.css'; // Make sure CSS is imported

// One-step application: a single submission creates the provider application
// (pending) and their first listing (pending) together. One admin approval
// flips both live and the listing appears in the public directory.
function ProviderApplication() {
  const { currentUser, isApprovedProvider } = useAuth();
  const navigate = useNavigate();

  const [applicationStatus, setApplicationStatus] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [geocodingError, setGeocodingError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [locationErrors, setLocationErrors] = useState({});
  // Title mirrors the business name until the user edits it directly
  const [titleEdited, setTitleEdited] = useState(false);

  const MAX_LOCATIONS = 5;

  const createEmptyLocation = () => ({
    id: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    address: '',
    coordinates: null
  });

  const [formData, setFormData] = useState({
    // Provider info
    businessName: '',
    category: '',
    phoneNumber: '',
    contact: currentUser?.email || '',
    socialMedia: {
      website: '',
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: ''
    },
    // Listing info
    title: '',
    description: '',
    location: '',
    isVirtual: false,
    price: '0',
    isFreeEvent: false,
    isContactForPrice: true, // default per spec
    coordinates: null,
    maxAttendees: '',
    locations: [createEmptyLocation()],
    eventStart: '',
    eventEnd: ''
  });

  // Check application status
  useEffect(() => {
    const checkApplicationStatus = async () => {
      if (!currentUser) {
           setPageLoading(false); // Stop loading if no user
           return;
      }

      // Reset state for potential re-fetches
      setApplicationStatus(null);
      setError(null);
      setPageLoading(true);

      try {
        // Use the isApprovedProvider flag from context first
        if (isApprovedProvider) {
          setApplicationStatus({ status: APPLICATION_STATUS.APPROVED }); // Set a status object for consistency
          setPageLoading(false);
          return;
        }

        // If not approved provider via context, check Firestore directly
        const statusData = await getApplicationStatus(currentUser.uid);
        setApplicationStatus(statusData); // statusData will be null or the application object
        setPageLoading(false);
      } catch (err) {
        console.error("Error checking application status:", err);
        setError("Failed to load application status. Please refresh the page or try again later.");
        setPageLoading(false);
      }
    };

    checkApplicationStatus();
  }, [currentUser, isApprovedProvider]); // Rerun if user or provider status changes


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle social media fields
    if (name.startsWith('social_')) {
      const platform = name.replace('social_', '');
      setFormData(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [platform]: value
        }
      }));
      return;
    }

    // Handle checkbox inputs
    if (type === 'checkbox') {
      setFormData(prev => {
        const next = {
          ...prev,
          [name]: checked,
          ...(name === 'isVirtual' && checked ? { coordinates: null } : {})
        };
        if (name === 'isVirtual' && !checked && prev.locations?.length) {
          next.location = prev.locations[0].address || '';
          next.coordinates = prev.locations[0].coordinates || null;
        }
        return next;
      });

      if (name === 'isVirtual' && checked) {
        setGeocodingError(null);
        setLocationErrors({});
      }

      // Free event and contact-for-price are mutually exclusive
      if (name === 'isFreeEvent' && checked) {
        setFormData(prev => ({
          ...prev,
          price: '0',
          isContactForPrice: false
        }));
      }

      if (name === 'isContactForPrice' && checked) {
        setFormData(prev => ({
          ...prev,
          price: '0',
          isFreeEvent: false
        }));
      }

      return;
    }

    if (name === 'location') {
      setFormData(prev => ({
        ...prev,
        location: value,
        coordinates: null
      }));
      setGeocodingError(null);
      return;
    }

    if (name === 'title') {
      setTitleEdited(true);
    }

    // Listing title defaults to the business name until edited directly
    if (name === 'businessName' && !titleEdited) {
      setFormData(prev => ({
        ...prev,
        businessName: value,
        title: value
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // If price is changed and not 0, uncheck both price-related checkboxes
    if (name === 'price' && value !== '0' && value !== '') {
      setFormData(prev => ({
        ...prev,
        isFreeEvent: false,
        isContactForPrice: false
      }));
    }
  };

  const updateLocationsState = (updater) => {
    setFormData(prev => {
      const nextLocations = updater(prev.locations || []);
      const ensuredLocations = nextLocations.length > 0 ? nextLocations : [createEmptyLocation()];
      const primary = ensuredLocations[0];
      return {
        ...prev,
        locations: ensuredLocations,
        location: prev.isVirtual ? prev.location : (primary.address || ''),
        coordinates: prev.isVirtual ? null : (primary.coordinates || null)
      };
    });
  };

  const handleLocationInputChange = (locationId, value) => {
    updateLocationsState(prevLocations =>
      prevLocations.map(loc =>
        loc.id === locationId ? { ...loc, address: value, coordinates: null } : loc
      )
    );
    setLocationErrors(prev => ({ ...prev, [locationId]: '' }));
    setGeocodingError(null);
  };

  const handleLocationSelect = (locationId, suggestion) => {
    updateLocationsState(prevLocations =>
      prevLocations.map(loc =>
        loc.id === locationId
          ? {
              ...loc,
              address: suggestion.displayName,
              coordinates: { lat: suggestion.lat, lng: suggestion.lng }
            }
          : loc
      )
    );
    setLocationErrors(prev => ({ ...prev, [locationId]: '' }));
    setGeocodingError(null);
  };

  const handleAddLocation = () => {
    updateLocationsState(prevLocations => {
      if (prevLocations.length >= MAX_LOCATIONS) {
        return prevLocations;
      }
      return [...prevLocations, createEmptyLocation()];
    });
  };

  const handleRemoveLocation = (locationId) => {
    updateLocationsState(prevLocations => {
      if (prevLocations.length === 1) return prevLocations;
      return prevLocations.filter(loc => loc.id !== locationId);
    });
    setLocationErrors(prev => {
      const next = { ...prev };
      delete next[locationId];
      return next;
    });
  };

  const handleMediaSelected = (files, previews) => {
    setMediaFiles(files);
    setUploadError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
        setError("You must be logged in to submit an application.");
        return;
    }

    setIsSubmitting(true);
    setError(null);
    setGeocodingError(null);
    setUploadError(null);

    try {
      // Validate event dates (both optional; ensure end is not before start if both provided)
      let startDate = null;
      if (formData.eventStart) {
        const parsedStart = new Date(formData.eventStart);
        if (!isNaN(parsedStart)) {
          startDate = parsedStart;
        }
      }
      let endDate = null;
      if (formData.eventEnd) {
        const parsedEnd = new Date(formData.eventEnd);
        if (!isNaN(parsedEnd)) {
          endDate = parsedEnd;
        }
        if (startDate && endDate && endDate < startDate) {
          setError('Event end must be after the start.');
          setIsSubmitting(false);
          return;
        }
      }

      // Process coordinates if not virtual
      let coordinates = null;
      let resolvedLocations = formData.locations || [];
      setLocationErrors({});

      if (!formData.isVirtual) {
        const nextErrors = {};
        const cleanedLocations = [];

        for (let i = 0; i < resolvedLocations.length; i++) {
          const locationEntry = resolvedLocations[i];
          const trimmedAddress = (locationEntry.address || '').trim();
          if (!trimmedAddress) {
            if (i === 0) {
              nextErrors[locationEntry.id] = 'Primary location is required.';
            }
            continue;
          }

          let entryCoordinates = locationEntry.coordinates;
          if (
            entryCoordinates &&
            typeof entryCoordinates.lat === 'number' &&
            typeof entryCoordinates.lng === 'number'
          ) {
            // keep as is
          } else {
            try {
              entryCoordinates = await geocodeAddress(trimmedAddress);
            } catch (geoError) {
              console.error("Geocoding error:", geoError);
              nextErrors[locationEntry.id] = "Couldn't verify this address. Try selecting a suggestion.";
            }
          }

          cleanedLocations.push({
            ...locationEntry,
            address: trimmedAddress,
            coordinates: entryCoordinates || null
          });

          if (!coordinates && entryCoordinates) {
            coordinates = entryCoordinates;
          }
        }

        if (cleanedLocations.length === 0) {
          nextErrors[
            resolvedLocations[0]?.id || 'primary'
          ] = 'Add at least one location.';
        }

        if (Object.keys(nextErrors).length > 0) {
          setLocationErrors(nextErrors);
          setGeocodingError("Please fix the highlighted locations before submitting.");
          setIsSubmitting(false);
          return;
        }

        resolvedLocations = cleanedLocations;
        if (!coordinates && resolvedLocations[0]?.coordinates) {
          coordinates = resolvedLocations[0].coordinates;
        }
      } else {
        resolvedLocations = [];
      }

      // Upload media files if provided
      let mediaData = [];
      if (mediaFiles.length > 0) {
        try {
          mediaData = await uploadMultipleMedia(mediaFiles);
        } catch (uploadErr) {
          console.error("Media upload error:", uploadErr);
          setUploadError(uploadErr.message || "Failed to upload media. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }

      // The pending listing created alongside the application; flips live on approval
      const listing = {
        title: formData.title || formData.businessName,
        description: formData.description,
        category: formData.category,
        location: formData.isVirtual
          ? formData.location
          : resolvedLocations[0]?.address || formData.location,
        isVirtual: formData.isVirtual,
        price: formData.price,
        isFreeEvent: formData.isFreeEvent,
        isContactForPrice: formData.isContactForPrice,
        contact: formData.contact,
        coordinates,
        locations: resolvedLocations,
        media: mediaData,
        maxAttendees: formData.maxAttendees ? parseInt(formData.maxAttendees) : null,
        eventStart: startDate,
        eventEnd: endDate,
        socialMedia: formData.socialMedia,
        providerName: formData.businessName,
        // Keep imageUrl for backward compatibility (use first image if available)
        imageUrl: mediaData.find(m => m.type === 'image')?.url || ''
      };

      await submitProviderApplication(currentUser.uid, {
        businessName: formData.businessName,
        businessType: formData.category,
        description: formData.description,
        phoneNumber: formData.phoneNumber,
        listing,
        // Add essential user info from currentUser for the application record
        email: currentUser.email,
        displayName: currentUser.displayName || 'User Application' // Add a fallback
      });

      // Re-fetch the application status after submission
      const status = await getApplicationStatus(currentUser.uid);
      setApplicationStatus(status);

    } catch (error) {
      console.error("Error submitting application:", error);
      setError("Failed to submit application. Please check your details and try again.");
      setIsSubmitting(false); // Set false only on error
    } finally {
         if (applicationStatus?.status !== APPLICATION_STATUS.PENDING) {
             setIsSubmitting(false);
         }
    }
  };

  // --- Render logic based on status ---

  // Use pageLoading state for the initial loading indicator
  if (pageLoading) {
      return <div className="provider-application-container" style={{textAlign: 'center', padding: '3rem'}}>Loading application status...</div>;
  }

   // Handle error state
   if (error && !applicationStatus) { // Show error prominently if loading failed
       return <div className="provider-application-container error-message">{error}</div>;
   }

   // User not logged in
   if (!currentUser) {
        return (
             <div className="provider-application-container" style={{textAlign: 'center', padding: '3rem'}}>
                 <h2>Please Sign In</h2>
                 <p>You need to be signed in to apply to become a service provider.</p>
                 <Link to="/signin" className="action-button">Sign In</Link>
             </div>
        );
   }


  // Already Approved Provider state (redundant check with context, but safe)
  if (applicationStatus?.status === APPLICATION_STATUS.APPROVED || isApprovedProvider) {
    return (
      <div className="provider-application-container">
        <div className="application-status approved">
          <h2>You are an approved service provider!</h2>
          <p>Your listing is live. You can add more listings and manage your services via the dashboard.</p>
          <button
            onClick={() => navigate('/add-listing')}
            className="action-button"
          >
            Add New Listing
          </button>
           <button
            onClick={() => navigate('/provider-dashboard')}
            className="action-button" style={{marginLeft: '1rem', backgroundColor: '#4a90e2'}}
          >
            View Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Pending Application state
  if (applicationStatus?.status === APPLICATION_STATUS.PENDING) {
    // Format date properly, ensuring it's a JavaScript Date object first
    const formatDate = (dateValue) => {
      if (!dateValue) return 'Recently';

      // If it's a Firestore timestamp with toDate method
      if (dateValue && typeof dateValue.toDate === 'function') {
        return dateValue.toDate().toLocaleDateString();
      }

      // If it's already a Date object
      if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString();
      }

      // If it's a string or number that can be parsed as a date
      const parsedDate = new Date(dateValue);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleDateString();
      }

      // Fallback
      return 'Recently';
    };

    return (
      <div className="provider-application-container">
        <div className="application-status pending">
          <h2>Application Under Review</h2>
          <p>Your application and listing are currently being reviewed by our administrators. Once approved, both go live automatically — your listing will appear in the directory right away. You'll be notified once a decision has been made (usually within 1-2 business days).</p>
          <p><strong>Submitted on:</strong> {formatDate(applicationStatus.submittedAt)}</p>
        </div>
      </div>
    );
  }

  // Rejected Application state
  if (applicationStatus?.status === APPLICATION_STATUS.REJECTED) {
    return (
      <div className="provider-application-container">
        <div className="application-status rejected">
          <h2>Application Not Approved</h2>
          <p>Unfortunately, your recent application to become a service provider was not approved.</p>
          {applicationStatus.rejectionReason && <p><strong>Reason:</strong> {applicationStatus.rejectionReason}</p>}
          <p>If you believe this was in error or have updated information, please <Link to="/contact">contact our support team</Link>.</p>
        </div>
      </div>
    );
  }

  // --- Default view: Unified Application + First Listing Form ---
  return (
    <div className="provider-application-container">
      <div className="provider-application-header">
        <h1>Become a Service Provider</h1>
        <p>One form, one approval. Tell us about your business and create your first listing — once our team approves it, your listing goes live in the directory automatically.</p>
      </div>

      {/* Display submit error if it exists */}
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="application-form">
        <h2 style={{ marginBottom: '1rem' }}>Provider Information</h2>

        <div className="form-group">
          <label htmlFor="businessName">Business or Service Name*</label>
          <input
            type="text"
            id="businessName"
            name="businessName"
            value={formData.businessName}
            onChange={handleChange}
            required
            className="form-control"
            maxLength={100}
          />
        </div>

        <div className="form-group">
          <label htmlFor="category">Type of Service / Category*</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            className="form-control"
          >
            <option value="">-- Select a category --</option>
            <option value="education">Education</option>
            <option value="recreation">Recreation</option>
            <option value="childcare">Childcare</option>
            <option value="mentorship">Mentorship</option>
            <option value="professional_development">Professional Development</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber">Business Phone Number*</label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            required
            className="form-control"
            placeholder="e.g., (123) 456-7890"
          />
        </div>

        <div className="form-group">
          <label htmlFor="contact">Contact Email*</label>
          <input
            type="email"
            id="contact"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            required
            className="form-control"
          />
        </div>

        {/* Social Media Section (optional) */}
        <div className="form-group" style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>Website & Social Links (Optional)</h3>

          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_website" style={{ fontSize: '0.9rem' }}>Website</label>
            <input
              type="url"
              id="social_website"
              name="social_website"
              value={formData.socialMedia.website}
              onChange={handleChange}
              placeholder="https://www.example.com"
              className="form-control"
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_facebook" style={{ fontSize: '0.9rem' }}>Facebook</label>
            <input
              type="url"
              id="social_facebook"
              name="social_facebook"
              value={formData.socialMedia.facebook}
              onChange={handleChange}
              placeholder="https://www.facebook.com/yourpage"
              className="form-control"
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_instagram" style={{ fontSize: '0.9rem' }}>Instagram</label>
            <input
              type="text"
              id="social_instagram"
              name="social_instagram"
              value={formData.socialMedia.instagram}
              onChange={handleChange}
              placeholder="@yourusername or https://www.instagram.com/yourusername"
              className="form-control"
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_twitter" style={{ fontSize: '0.9rem' }}>Twitter/X</label>
            <input
              type="text"
              id="social_twitter"
              name="social_twitter"
              value={formData.socialMedia.twitter}
              onChange={handleChange}
              placeholder="@yourusername or https://twitter.com/yourusername"
              className="form-control"
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_linkedin" style={{ fontSize: '0.9rem' }}>LinkedIn</label>
            <input
              type="url"
              id="social_linkedin"
              name="social_linkedin"
              value={formData.socialMedia.linkedin}
              onChange={handleChange}
              placeholder="https://www.linkedin.com/in/yourprofile"
              className="form-control"
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label htmlFor="social_youtube" style={{ fontSize: '0.9rem' }}>YouTube</label>
            <input
              type="url"
              id="social_youtube"
              name="social_youtube"
              value={formData.socialMedia.youtube}
              onChange={handleChange}
              placeholder="https://www.youtube.com/channel/yourchannel"
              className="form-control"
            />
          </div>
        </div>

        <h2 style={{ margin: '2rem 0 1rem' }}>Listing Information</h2>

        <div className="form-group">
          <label htmlFor="title">Listing Title*</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="form-control"
            maxLength={100}
            placeholder="Defaults to your business name — edit if you like"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description*</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows="6"
            className="form-control"
            style={{ whiteSpace: 'pre-wrap' }}
            placeholder="Describe your services — what you offer, target age group, credentials, and anything else families should know. Line breaks will be preserved."
          ></textarea>
        </div>

        <div className="form-group">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              id="isVirtual"
              name="isVirtual"
              checked={formData.isVirtual}
              onChange={handleChange}
              style={{ marginRight: '0.5rem' }}
            />
            <label htmlFor="isVirtual" style={{ marginBottom: 0 }}>This is a virtual listing (online only)</label>
          </div>
        </div>

        {!formData.isVirtual && (
          <div className="form-group">
            <label>Locations*</label>
            <p className="form-note" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
              Add one or more addresses. The first location is treated as the primary map pin. Online only? Check the virtual listing box above.
            </p>
            {formData.locations.map((loc, index) => (
              <div
                key={loc.id}
                style={{
                  marginBottom: '0.75rem',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '0.75rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                  <strong>Location {index + 1}</strong>
                  {index === 0 && <span style={{ fontSize: '0.8rem', color: '#999' }}>Primary</span>}
                </div>
                <LocationAutocomplete
                  id={`location-${loc.id}`}
                  name={`location-${loc.id}`}
                  value={loc.address}
                  onChange={(value) => handleLocationInputChange(loc.id, value)}
                  onSelect={(suggestion) => handleLocationSelect(loc.id, suggestion)}
                  required={index === 0}
                  disabled={isSubmitting}
                  placeholder="Enter a full address (123 Main St, City, Province/State, Country)"
                />
                {locationErrors[loc.id] && (
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#e74c3c' }}>
                    {locationErrors[loc.id]}
                  </small>
                )}
                {!locationErrors[loc.id] && loc.address && !loc.coordinates && (
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#666' }}>
                    Tip: select a suggestion to pin this address on the map.
                  </small>
                )}
                {!locationErrors[loc.id] && loc.coordinates && (
                  <small style={{ display: 'block', marginTop: '0.35rem', color: '#2ecc71' }}>
                    Coordinates saved ({loc.coordinates.lat.toFixed(4)}, {loc.coordinates.lng.toFixed(4)}).
                  </small>
                )}
                {formData.locations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveLocation(loc.id)}
                    style={{
                      marginTop: '0.5rem',
                      background: 'transparent',
                      border: 'none',
                      color: '#c0392b',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            {formData.locations.length < MAX_LOCATIONS && (
              <button
                type="button"
                onClick={handleAddLocation}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  backgroundColor: '#f1f5f9',
                  border: '1px dashed #b0bec5',
                  borderRadius: '4px',
                  padding: '0.4rem 0.75rem',
                  cursor: 'pointer'
                }}
              >
                + Add Another Location
              </button>
            )}
            {geocodingError && (
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#e74c3c' }}>
                {geocodingError}
              </small>
            )}
          </div>
        )}

        {formData.isVirtual && (
          <div className="form-group">
            <label htmlFor="location">Virtual Location Details (Optional)</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Zoom, Google Meet, Online Platform"
              className="form-control"
            />
          </div>
        )}

        {/* Event Date/Time */}
        <div className="form-group">
          <label>When does the event occur? (Optional)</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label htmlFor="eventStart" style={{ fontSize: '0.9rem' }}>Start</label>
              <input
                type="datetime-local"
                id="eventStart"
                name="eventStart"
                value={formData.eventStart}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div>
              <label htmlFor="eventEnd" style={{ fontSize: '0.9rem' }}>End</label>
              <input
                type="datetime-local"
                id="eventEnd"
                name="eventEnd"
                value={formData.eventEnd}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Price Options</label>

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              id="isContactForPrice"
              name="isContactForPrice"
              checked={formData.isContactForPrice}
              onChange={handleChange}
              style={{ marginRight: '0.5rem' }}
            />
            <label htmlFor="isContactForPrice" style={{ marginBottom: 0 }}>Contact for pricing</label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <input
              type="checkbox"
              id="isFreeEvent"
              name="isFreeEvent"
              checked={formData.isFreeEvent}
              onChange={handleChange}
              style={{ marginRight: '0.5rem' }}
            />
            <label htmlFor="isFreeEvent" style={{ marginBottom: 0 }}>This is a free event</label>
          </div>

          {!formData.isFreeEvent && !formData.isContactForPrice && (
            <div>
              <label htmlFor="price">Price ($)</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                min="0"
                step="0.01"
                required={!formData.isFreeEvent && !formData.isContactForPrice}
                placeholder="0.00"
                className="form-control"
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="maxAttendees">Maximum Attendees (Optional)</label>
          <input
            type="number"
            id="maxAttendees"
            name="maxAttendees"
            value={formData.maxAttendees}
            onChange={handleChange}
            min="1"
            placeholder="Leave empty for unlimited"
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label>Upload Media (Optional)</label>
          <MultiMediaUpload
            onMediaSelected={handleMediaSelected}
            existingMedia={[]}
          />
          {uploadError && (
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#e74c3c' }}>
              {uploadError}
            </small>
          )}
        </div>

        <div className="form-group">
          <p className="form-note" style={{textAlign: 'right'}}>* Required fields</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="submit-button"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Application'}
        </button>
      </form>
    </div>
  );
}

export default ProviderApplication;
